import { ARBITRUM_USDT_ADDRESS, USDT_DECIMALS } from "./useArbitrumUsdtBalance";
import { ERC20_ABI } from "@/constants/onchain-swap";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSendTransactionWithConnection } from "@/hooks/core/useSendTransactionWithConnection";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { encodeFunctionData, isAddress, parseUnits } from "viem";
import { usePublicClient, useAccount } from "wagmi";
import { arbitrum } from "wagmi/chains";

export const useTransferUsdtToCex = () => {
  const { sendTransactionAsync } = useSendTransactionWithConnection();
  const publicClient = usePublicClient({ chainId: arbitrum.id });
  const { address } = useAccount();
  const { authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ amount, recipientAddress }: { amount: string; recipientAddress: string }) => {
      if (!publicClient) throw new Error("Public client not available");
      if (!isAddress(recipientAddress)) throw new Error("Invalid recipient address");

      const amountInWei = parseUnits(amount, USDT_DECIMALS);

      const data = encodeFunctionData({
        abi: ERC20_ABI,
        functionName: "transfer",
        args: [recipientAddress, amountInWei],
      });

      const txHash = await sendTransactionAsync({
        to: ARBITRUM_USDT_ADDRESS,
        data,
      });

      await publicClient.waitForTransactionReceipt({ hash: txHash });

      queryClient.invalidateQueries({ queryKey: ["usdtBalance", authenticatedWalletAddress ?? address] });

      return txHash;
    },
  });
};
